import { Box, Button, Chip, Collapse, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { FormApi } from "final-form";
import { SelectData, Switches } from "mui-rff";
import { useMemo } from "react";
import { Form } from "react-final-form";

import { useTranslation } from "@/lib/i18n";
import {
  useAdminDashboard,
  useExtranetsQuery,
  useUpdateDashboardMutation,
} from "@/features/admin/hooks";
import { showToast, TextField, Select } from "@/features/shared/components";

interface FormValues {
  name: string;
  active: boolean;
  extranet: string;
}

const InfoSettings = () => {
  const { t } = useTranslation();
  const updateDashboardMutation = useUpdateDashboardMutation();
  const { id, name, active, extranet } = useAdminDashboard();
  const extranets = useExtranetsQuery().data ?? [];

  async function onSubmit(values: FormValues, form: FormApi<FormValues>) {
    updateDashboardMutation.mutate(
      { id, body: values },
      {
        onSuccess: () => {
          showToast(t("admin.messages.dashboard_updated"));
          form.reset(values);
        },
      },
    );
  }

  const extranetsOptions = useMemo<SelectData[]>(
    () =>
      extranets.map(({ name, id }) => ({
        label: name,
        value: id,
      })) ?? [],
    [extranets],
  );

  return (
    <Form<FormValues>
      onSubmit={onSubmit}
      initialValues={{ name, active, extranet: extranet.id }}
      render={({ handleSubmit, values, pristine, initialValues }) => (
        <Stack spacing={4} sx={{ py: 1 }}>
          <Box>
            <TextField
              margin="dense"
              name="name"
              label={t("admin.dashboard_settings.fields.name.label")}
              required
              variant="standard"
            />
            <Collapse in={values.name !== initialValues.name}>
              <Chip
                sx={{ mt: 2 }}
                variant="outlined"
                label={t("admin.dashboard_settings.change_name_message")}
                color="primary"
              />
            </Collapse>
          </Box>
          <Select
            required
            disabled
            name="extranet"
            label={t("admin.new_dashboard_dialog.extranet_field.label")}
            options={extranetsOptions}
            value={extranetsOptions.length ? values.extranet : ""}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ px: "2px" }}
          >
            <Typography>
              <>
                {t(
                  `admin.dashboard_settings.fields.active.label_${
                    values.active ? "active" : "inactive"
                  }`,
                )}
                <small style={{ color: grey[500] }}>
                  <br />
                  {t("admin.dashboard_settings.fields.active.note")}
                </small>
              </>
            </Typography>

            <Switches data={{ label: "", value: "available" }} name="active" />
          </Stack>
          <Stack alignItems="flex-end">
            <Button disabled={pristine} onClick={handleSubmit} variant="outlined">
              {t("general.save")}
            </Button>
          </Stack>
        </Stack>
      )}
    />
  );
};

export default InfoSettings;
